import { useMemo, useState } from 'react'
import { ChevronLeft } from 'lucide-react'
import type { ActivityType, CurrentPractice, PrimaryGoal } from '@movmania/core'
import {
  ACTIVITY_TYPES,
  CURRENT_PRACTICES,
  MAX_WEEKLY_GOAL_MIN,
  MIN_WEEKLY_GOAL_MIN,
  PRIMARY_GOALS,
  WHO_WEEKLY_MINUTES,
  suggestedWeeklyGoal,
} from '@movmania/core'
import {
  Button,
  Card,
  Figure,
  IconButton,
  Screen,
  ScreenBody,
  ScreenFooter,
} from '../components/Ui.js'
import { ActivityTileGrid } from '../components/ActivityTile.js'
import { photo } from '../lib/photos.js'
import { useT } from '../lib/useT.js'
import { useStore } from '../store/useStore.js'

type Step = 'welcome' | 'favorites' | 'practice' | 'body' | 'goal' | 'target' | 'name'

const STEPS: Step[] = [
  'welcome',
  'favorites',
  'practice',
  'body',
  'goal',
  'target',
  'name',
]

export function Onboarding() {
  const t = useT()
  const completeOnboarding = useStore((s) => s.completeOnboarding)

  const [index, setIndex] = useState(0)
  const [favorites, setFavorites] = useState<ActivityType[]>([])
  const [practice, setPractice] = useState<CurrentPractice | null>(null)
  const [age, setAge] = useState('')
  const [height, setHeight] = useState('')
  const [weight, setWeight] = useState('')
  const [goal, setGoal] = useState<PrimaryGoal | null>(null)
  const [target, setTarget] = useState<number | null>(null)
  const [name, setName] = useState('')

  const step = STEPS[index]!

  // L'objectif proposé découle de la pratique déclarée : on ne montre 150 min
  // qu'à quelqu'un qui bouge déjà régulièrement.
  const suggested = useMemo(
    () => (practice ? suggestedWeeklyGoal(practice) : WHO_WEEKLY_MINUTES),
    [practice],
  )
  const effectiveTarget = target ?? suggested

  const valid: Record<Step, boolean> = {
    welcome: true,
    favorites: favorites.length > 0,
    practice: practice !== null,
    body: true,
    goal: goal !== null,
    target: effectiveTarget >= MIN_WEEKLY_GOAL_MIN,
    name: name.trim().length > 0,
  }

  function next() {
    if (index < STEPS.length - 1) {
      setIndex((value) => value + 1)
      return
    }
    completeOnboarding({
      displayName: name.trim(),
      favoriteActivities: favorites,
      currentPractice: practice ?? 'occasional',
      primaryGoal: goal ?? 'fitness',
      weeklyGoalMin: effectiveTarget,
      ...(age ? { birthYear: new Date().getFullYear() - Number(age) } : {}),
      ...(height ? { heightCm: Number(height) } : {}),
      ...(weight ? { weightKg: Number(weight) } : {}),
    })
  }

  return (
    <Screen>
      <ScreenBody className="safe-top flex flex-col">
      <div className="flex min-h-12 items-center gap-2">
        {index > 0 && (
          <IconButton
            icon={ChevronLeft}
            label={t('common.back')}
            onClick={() => setIndex((value) => value - 1)}
          />
        )}
        <div className="bg-sunken ml-auto h-2 w-28 overflow-hidden rounded-full">
          <div
            className="bg-green h-full rounded-full transition-[width] duration-300 ease-out"
            style={{ width: `${((index + 1) / STEPS.length) * 100}%` }}
          />
        </div>
      </div>

      <div key={step} className="animate-rise flex flex-1 flex-col justify-center py-8">
        {step === 'welcome' && (
          <div>
            {/* Feuillage sur fond crème : le texte se pose dans la zone claire. */}
            <div className="relative mb-8 h-64 overflow-hidden rounded-[var(--radius-card)] shadow-[var(--shadow-soft)]">
              <img src={photo('leaves')} alt="" className="absolute inset-0 size-full object-cover" />
              <p className="text-title text-green absolute inset-0 grid place-items-center font-bold">
                {t('common.appName')}
              </p>
            </div>
            <h1 className="text-display font-bold">{t('onboarding.welcomeTitle')}</h1>
            <p className="text-body text-ink-2 mt-4">{t('onboarding.welcomeBody')}</p>
          </div>
        )}

        {step === 'favorites' && (
          <Question title={t('onboarding.favoritesTitle')} body={t('onboarding.favoritesBody')}>
            <ActivityTileGrid
              types={ACTIVITY_TYPES}
              isSelected={(type) => favorites.includes(type)}
              onToggle={(type) =>
                setFavorites((current) =>
                  current.includes(type)
                    ? current.filter((item) => item !== type)
                    : [...current, type],
                )
              }
            />
          </Question>
        )}

        {step === 'practice' && (
          <Question title={t('onboarding.practiceTitle')} body={t('onboarding.practiceBody')}>
            <div className="flex flex-col gap-2">
              {CURRENT_PRACTICES.map((option) => (
                <Option
                  key={option}
                  selected={practice === option}
                  onClick={() => {
                    setPractice(option)
                    setTarget(null)
                  }}
                  title={t(`onboarding.practice${cap(option)}`)}
                  hint={t(`onboarding.practice${cap(option)}Hint`)}
                />
              ))}
            </div>
          </Question>
        )}

        {step === 'body' && (
          <Question title={t('onboarding.bodyTitle')} body={t('onboarding.bodyBody')}>
            <div className="flex flex-col gap-2">
              <NumberField label={t('onboarding.bodyAge')} unit={t('onboarding.bodyYears')} value={age} onChange={setAge} min={10} max={110} />
              <NumberField label={t('onboarding.bodyHeight')} unit="cm" value={height} onChange={setHeight} min={100} max={230} />
              <NumberField label={t('onboarding.bodyWeight')} unit="kg" value={weight} onChange={setWeight} min={30} max={250} />
            </div>
          </Question>
        )}

        {step === 'goal' && (
          <Question title={t('onboarding.goalTitle')} body={t('onboarding.goalBody')}>
            <div className="flex flex-col gap-2">
              {PRIMARY_GOALS.map((option) => (
                <Option
                  key={option}
                  selected={goal === option}
                  onClick={() => setGoal(option)}
                  title={t(`primaryGoal.${option}`)}
                />
              ))}
            </div>
          </Question>
        )}

        {step === 'target' && (
          <Question title={t('onboarding.targetTitle')} body={t('onboarding.targetBody')}>
            <Card className="text-center">
              <Figure value={effectiveTarget} unit="min" size="display" />
              <input
                type="range"
                min={MIN_WEEKLY_GOAL_MIN}
                max={MAX_WEEKLY_GOAL_MIN}
                step={15}
                value={effectiveTarget}
                onChange={(event) => setTarget(Number(event.target.value))}
                className="slider mt-4"
                aria-label={t('onboarding.targetTitle')}
              />
            </Card>
            <p className="text-caption text-ink-3 mt-4 text-center">
              {t('onboarding.targetWho', { count: WHO_WEEKLY_MINUTES })} ·{' '}
              <span className="text-ink-2">
                {effectiveTarget < WHO_WEEKLY_MINUTES
                  ? t('onboarding.targetBelowWho')
                  : effectiveTarget === WHO_WEEKLY_MINUTES
                    ? t('onboarding.targetAtWho')
                    : t('onboarding.targetAboveWho')}
              </span>
            </p>
          </Question>
        )}

        {step === 'name' && (
          <Question title={t('onboarding.nameTitle')}>
            <input
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder={t('onboarding.namePlaceholder')}
              autoFocus
              maxLength={24}
              className="card text-body focus:outline-green min-h-14 w-full !rounded-full px-6 font-bold"
            />
          </Question>
        )}
      </div>

      </ScreenBody>

      <ScreenFooter safe>
        <Button full disabled={!valid[step]} onClick={next}>
          {step === 'welcome'
            ? t('onboarding.welcomeStart')
            : step === 'name'
              ? t('onboarding.finish')
              : t('common.continue')}
        </Button>
      </ScreenFooter>
    </Screen>
  )
}

function cap(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1)
}

function Question({
  title,
  body,
  children,
}: {
  title: string
  body?: string
  children: React.ReactNode
}) {
  return (
    <div>
      <h1 className="text-title font-bold">{title}</h1>
      {body && <p className="text-body text-ink-2 mt-2">{body}</p>}
      <div className="mt-6">{children}</div>
    </div>
  )
}

function Option({
  selected,
  onClick,
  title,
  hint,
}: {
  selected: boolean
  onClick: () => void
  title: string
  hint?: string
}) {
  return (
    <button
      onClick={onClick}
      aria-pressed={selected}
      className={`press flex min-h-14 flex-col justify-center rounded-[var(--radius-tile)] px-5 py-3 text-left ${
        selected ? 'bg-green text-surface' : 'bg-surface text-ink shadow-[var(--shadow-soft)]'
      }`}
    >
      <span className="text-body font-bold">{title}</span>
      {hint && (
        <span className={`text-caption ${selected ? 'text-surface/80' : 'text-ink-3'}`}>
          {hint}
        </span>
      )}
    </button>
  )
}

function NumberField({
  label,
  unit,
  value,
  onChange,
  min,
  max,
}: {
  label: string
  unit: string
  value: string
  onChange: (value: string) => void
  min: number
  max: number
}) {
  return (
    <label className="card flex min-h-14 items-center gap-3 !rounded-[var(--radius-tile)] px-5">
      <span className="text-body text-ink-2 flex-1">{label}</span>
      <input
        type="number"
        inputMode="numeric"
        value={value}
        min={min}
        max={max}
        onChange={(event) => onChange(event.target.value)}
        placeholder="—"
        className="text-body num w-16 bg-transparent text-right font-bold outline-none"
      />
      <span className="text-caption text-ink-3 w-8">{unit}</span>
    </label>
  )
}
